import { isEqual } from 'lodash';
import { TARGET_TYPES } from 'app/forms/config/unified/constants';
import {
  POLICY_APPLICATIONS,
  SEVERITY_MAP,
  EVENT_POLICY_TYPES,
  ALERT_MANAGER_ACTIVATION_MODES,
  ALERT_MANAGER_SEVERITY_TO_STANDARD_ALERT_SEVERITY,
  FLOW_THRESHOLD_STATUS
} from '@kentik/ui-shared/alerting/constants';
import {
  isSyslogEventPolicy,
  isSnmpTrapEventPolicy,
  getDefaultEventAppProtoFilter
} from 'app/stores/alerting/policyUtils';
import { defaultSerializer } from 'app/util/nms/serializations';
import policyThresholdsSerialization from 'app/stores/alerting/policySerializations/policyThresholdsSerialization';

const DEFAULT_KEVENT_POLICY = {
  application: POLICY_APPLICATIONS.KEVENT,
  metrics: ['fps'],
  clearOverride: null,
  evaluationPeriod: '60s',
  nTopKeysEvaluated: '1000',
  nTopKeysAddedToBaseline: '0',
  minTrafficValue: 0,
  baselineSettings: {
    datapointAggregationMethod: 'REL_P25',
    startOffset: '86400s',
    length: '518400s',
    minimalLength: '86400s',
    seasonality: 'hourly',
    neighbourhoodAggregationMethod: 'None',
    neighbourhoodRadius: 0,
    comparisonValueAggregateFunction: 'REL_P95',
    weekendAware: true
  }
};

const DEFAULT_KEVENT_CONDITION = {
  metric: 'fps',
  operator: 'greaterThanOrEquals',
  comparisonValue: 0,
  type: 'static',
  direction: 'thresholdDirectionNone',
  fallbackSettings: null,
  keySettings: null,
  ratioSettings: null
};

const serializeActivationSettings = (policy) => {
  const { activationSettings = {} } = policy || {};

  return {
    ...activationSettings,
    mode: activationSettings.mode || ALERT_MANAGER_ACTIVATION_MODES.TOGGLE
  };
};

const serializeThresholdFilters = (filters = {}) => {
  const transformedConjunctions = (filters?.conjunctions || []).map((conjunction = {}) => ({
    ...conjunction,
    entries: (conjunction?.entries || []).map((entry = {}) => {
      if (Array.isArray(entry.patterns) && entry?.patterns?.filter(Boolean)?.length > 0) {
        delete entry.values;
      } else {
        delete entry.patterns;
      }
      return entry;
    })
  }));

  // isInclude determines whether filter matches (true) or excludes (false) traffic.
  // If it is omitted, it will be set to true by default.
  return { ...filters, conjunctions: transformedConjunctions, isInclude: true };
};

const serializeThresholds = (policy) => {
  const { thresholds } = policy || {};

  if (!thresholds || thresholds.length === 0) {
    return [];
  }

  return thresholds
    .filter((t) => t.uiProperties?.enabled)
    .map(({ enabled, policyID, thresholdID, ...threshold }) => {
      const serialized = {
        ...threshold,
        status: threshold.uiProperties?.enabled ? FLOW_THRESHOLD_STATUS.ACTIVE : FLOW_THRESHOLD_STATUS.DISABLED,
        conditions: [DEFAULT_KEVENT_CONDITION],
        filters: serializeThresholdFilters(threshold.filters)
      };

      return policyThresholdsSerialization.serializeThresholdActivate(serialized);
    });
};

const serializeFilterGroups = (policy) => {
  if (policy.filters?.filterGroups?.length > 0) {
    return policy.filters.filterGroups;
  }

  // Unless a user provides one, we use a default filter to improve performance
  const defaultAppProtoFilter = getDefaultEventAppProtoFilter(policy.dimensions);

  return [{ connector: 'all', filters: [defaultAppProtoFilter] }];
};

const deserializeFilterGroups = (policy) => {
  if (!Array.isArray(policy.filters?.filterGroups)) {
    return [];
  }

  if (policy.filters.filterGroups.length !== 1) {
    return policy.filters.filterGroups;
  }

  // If no policy filter is provided at creation, we add our own "timestamp > 0" filter.
  // This reduces the data processed by a policy by scoping it to the right AppProtocol.
  // However, we don't want this filter showing to users, or being applied to chart queries.

  const appProtoFilter = getDefaultEventAppProtoFilter(policy.dimensions);

  return policy.filters.filterGroups.filter(({ filters }) => {
    const hasOneFilter = filters.length === 1;
    const hasTimestampFilter = isEqual(filters[0], appProtoFilter);
    // Only reject the filter group if it has a single filter, and that filter is the appProtoFilter
    return !(hasOneFilter && hasTimestampFilter);
  });
};

const deserializeThresholds = (policy) => {
  const { thresholds, rule = {} } = policy || {};

  if (!thresholds || thresholds.length === 0) {
    const defaultThreshold = {
      uiProperties: {
        enabled: true
      },
      enabled: true, // This is a holdover until we rewrite PolicySummaryPanel
      severity: SEVERITY_MAP.MAJOR.STANDARD_ALERT,
      direction: 'currentToHistory',
      conditions: [],
      ackRequired: false,
      filters: null,
      mitigations: [],
      notificationChannels: []
    };

    // Derive activate defaults from the policy's activation mode (toggle vs rolling window)
    return [policyThresholdsSerialization.deserializeThresholdActivate(defaultThreshold, policy)];
  }

  return thresholds.map((threshold) => {
    const ruleLevel = (rule.levels || []).find(
      (level) => ALERT_MANAGER_SEVERITY_TO_STANDARD_ALERT_SEVERITY[level.severity] === threshold.severity
    );

    const deserializedThreshold = {
      ...threshold,
      uiProperties: {
        enabled: threshold.status ? threshold.status === FLOW_THRESHOLD_STATUS.ACTIVE : (threshold.enabled ?? true)
      }
    };

    if (ruleLevel) {
      deserializedThreshold.ackRequired = ruleLevel.ack_required;
    }

    return policyThresholdsSerialization.deserializeThresholdActivate(deserializedThreshold, policy, ruleLevel);
  });
};

const deserializeEventType = (policy) => {
  const { dimensions = [] } = policy || {};

  if (isSnmpTrapEventPolicy(dimensions)) {
    return EVENT_POLICY_TYPES.SNMP_TRAP;
  }

  if (isSyslogEventPolicy(dimensions)) {
    return EVENT_POLICY_TYPES.SYSLOG;
  }

  // Redundant, but prefer being verbose
  return EVENT_POLICY_TYPES.SYSLOG;
};

const deserialize = (apiPolicy) => {
  if (!apiPolicy) {
    return {};
  }

  const activationSettings = {
    ...apiPolicy.activationSettings,
    mode: apiPolicy.activationSettings?.mode || ALERT_MANAGER_ACTIVATION_MODES.TOGGLE,
    activationDelay: apiPolicy.activationSettings?.activationDelay || 0,
    clearanceDelay: apiPolicy.activationSettings?.clearanceDelay || 0
  };

  // Pass resolved activationSettings into threshold deserialization so default activate
  // values (toggle vs rolling window) match the policy's mode.
  const policyWithActivation = { ...apiPolicy, activationSettings };

  return {
    ...apiPolicy,
    application: POLICY_APPLICATIONS.KEVENT,
    metrics: ['fps'],
    evaluationPeriod: parseInt(String(apiPolicy.evaluationPeriod || '0').replace('s', ''), 10),
    thresholds: deserializeThresholds(policyWithActivation),
    filters: {
      connector: apiPolicy.filters?.connector || 'all',
      filterGroups: deserializeFilterGroups(apiPolicy)
    },
    activationSettings,
    uiProperties: {
      targetType: TARGET_TYPES.EVENT,
      eventType: deserializeEventType(apiPolicy)
    }
  };
};

const serialize = (policyAttrs) => {
  if (!policyAttrs) {
    return {};
  }

  const activationSettings = serializeActivationSettings(policyAttrs);
  const isToggleMode = activationSettings.mode === ALERT_MANAGER_ACTIVATION_MODES.TOGGLE;

  return defaultSerializer(
    {
      ...DEFAULT_KEVENT_POLICY,
      ...policyAttrs,
      evaluationPeriod: isToggleMode ? '0s' : '60s',
      filters: {
        connector: policyAttrs.filters?.connector || 'all',
        filterGroups: serializeFilterGroups(policyAttrs)
      },
      thresholds: serializeThresholds(policyAttrs),
      activationSettings
    },
    ['uiProperties']
  );
};

export { deserialize, serialize };
